import React from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

type TypeClient =
  | "entreprise"
  | "menage"
  | "institution"
  | "chantier"
  | undefined;

interface Props {
  missionId: number;
  date?: string | Date;
  zone?: string;
  quartier?: string;
  client?: string;
  clientId?: number;
  typeClient?: TypeClient;
  lieu?: string;
  latitude?: number;
  longitude?: number;
}

const formatDate = (value?: string | Date) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("fr-FR");
};

const display = (value?: string) =>
  value && value.trim() !== "" ? value : "—";

const getClientBadgeStyle = (type?: TypeClient) => {
  switch (type) {
    case "entreprise":
      return "bg-blue-600/20 text-blue-400";
    case "menage":
      return "bg-green-600/20 text-green-400";
    case "institution":
      return "bg-purple-600/20 text-purple-400";
    case "chantier":
      return "bg-orange-600/20 text-orange-400";
    default:
      return "bg-slate-600/20 text-slate-400";
  }
};

const getClientLabel = (type?: TypeClient) => {
  switch (type) {
    case "entreprise":
      return "Entreprise";
    case "menage":
      return "Ménage";
    case "institution":
      return "Institution";
    case "chantier":
      return "Chantier";
    default:
      return "—";
  }
};

const MissionInfoCard: React.FC<Props> = ({
  missionId,
  date,
  zone,
  quartier,
  client,
  clientId,
  typeClient,
  lieu,
  latitude,
  longitude,
}) => {
  const navigate = useNavigate();

  if (!missionId || isNaN(missionId)) {
    return null;
  }

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "";

  const qrValue = `${origin}/mission/${missionId}`;

  const hasValidGPS =
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    !isNaN(latitude) &&
    !isNaN(longitude);

  const googleMapsUrl = hasValidGPS
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : null;

  return (
    <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-700 shadow-md space-y-4">
      <h2 className="font-semibold text-base text-gray-200">
        🧾 Informations générales
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm text-gray-300">

        <div>
          <span className="font-medium text-gray-400">📅 Date :</span>{" "}
          {formatDate(date)}
        </div>

        <div>
          <span className="font-medium text-gray-400">🌍 Zone :</span>{" "}
          {display(zone)}
        </div>

        <div>
          <span className="font-medium text-gray-400">🏘 Quartier :</span>{" "}
          {display(quartier)}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-400">🏢 Client :</span>
          <span>{display(client)}</span>

          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${getClientBadgeStyle(
              typeClient
            )}`}
          >
            {getClientLabel(typeClient)}
          </span>
        </div>

        {lieu && (
          <div className="sm:col-span-2">
            <span className="font-medium text-gray-400">📍 Lieu :</span>{" "}
            {display(lieu)}
          </div>
        )}

        {hasValidGPS && (
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <span className="font-medium text-gray-400">
              🛰 Coordonnées GPS :
            </span>
            <span>
              {latitude!.toFixed(5)}, {longitude!.toFixed(5)}
            </span>

            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline text-xs"
              >
                📍 Ouvrir dans Google Maps
              </a>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-slate-700">

        {clientId && (
          <button
            onClick={() => navigate(`/admin/clients/${clientId}`)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm transition"
          >
            🔍 Voir fiche client
          </button>
        )}

        <div className="text-center">
          <QRCodeSVG
            value={qrValue}
            size={90}
            bgColor="#0f172a"
            fgColor="#38bdf8"
            level="H"
          />
          <div className="text-xs text-gray-500 mt-1">
            QR Mission #{missionId}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionInfoCard;